import ActionDisabledTooltip from '@/components/buttons/ActionDisabledTooltip';
import { TransferError } from '@/components/buttons/Transfer/TransferError';
import { TransferLoading } from '@/components/buttons/Transfer/TransferLoading';
import { TransferSuccess } from '@/components/buttons/Transfer/TransferSuccess';
import { QuantityPickerBox } from '@/components/shared/QuantityPickerBox';
import { useMarketplaceChain } from '@/hooks/use-marketplace-chain';
import { useAuthentication } from '@/hooks/useAuthentication';
import { useEstimateGas } from '@/hooks/useEstimateGas';
import { wagmiConfig } from '@/lib/wagmi';
import { ReservoirAction } from '@/utils/reservoir';
import {
  Box,
  Button,
  chakra,
  Flex,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Heading,
  Input,
  Text,
} from '@chakra-ui/react';
import { zodResolver } from '@hookform/resolvers/zod';
import { Modal } from '@sphere/ui';
import { waitForTransactionReceipt } from '@wagmi/core';
import useTranslation from 'next-translate/useTranslation';
import { rem } from 'polished';
import { cloneElement, forwardRef, useCallback, useState } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import { Address, encodeFunctionData, erc1155Abi, erc721Abi, Hex } from 'viem';
import { useAccount, useSwitchChain, useWalletClient } from 'wagmi';
import { TransferFormSchemaType, TransferProps } from './Transfer.types';
import { transferValidationSchema } from './Transfer.utils';

const transferStatusMap = {
  error: TransferError,
  success: TransferSuccess,
  pending: TransferLoading,
  idle: () => null,
};

export const Transfer = ({
  tokenName,
  contractAddress,
  tokenId,
  trigger,
  ownedQuantity,
  is1155,
  onClose,
  openState: customOpenState,
}: TransferProps) => {
  const { t } = useTranslation('common');
  const defaultOpenState = useState(false);
  const [isOpen, setOpen] = customOpenState || defaultOpenState;
  const { userAddress } = useAuthentication();
  const marketplaceChain = useMarketplaceChain();
  const { chain } = useAccount();
  const { switchChain } = useSwitchChain();
  const [transactionHash, setTransactionHash] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const { data: walletClient } = useWalletClient({ chainId: marketplaceChain.id });

  const {
    register,
    handleSubmit,
    reset: resetForm,
    watch,
    formState: { isSubmitting, isSubmitSuccessful, errors },
    setValue,
  } = useForm<TransferFormSchemaType>({
    mode: 'onBlur',
    defaultValues: { quantity: 1 },
    resolver: zodResolver(transferValidationSchema(t, userAddress, ownedQuantity)),
  });

  const values = watch();

  const getParams = ({ address, quantity }: TransferFormSchemaType) => ({
    abi: is1155 ? erc1155Abi : erc721Abi,
    address: contractAddress as Address,
    functionName: 'safeTransferFrom',
    chainId: marketplaceChain.id,
    args: [
      userAddress,
      address,
      tokenId,
      ...(is1155 ? [BigInt(Number(quantity) || 1) as bigint, '' as `0x${string}`] : []),
    ],
  });

  const { deficit: gasFeeDeficit, showGasWarning } = useEstimateGas(
    getParams({ quantity: 1, address: userAddress! }),
    { isEnabled: isOpen },
  );

  const onSubmit: SubmitHandler<TransferFormSchemaType> = useCallback(
    async ({ address, quantity }) => {
      if (!walletClient) return;

      const baseArgs = [userAddress as Address, address, BigInt(tokenId)] as const;

      const transferArgs = is1155
        ? ([...baseArgs, BigInt(Number(quantity) || 1), '' as Hex] as const)
        : baseArgs;

      try {
        const hash = await walletClient.sendTransaction({
          chain: marketplaceChain,
          account: userAddress!,
          to: contractAddress as Address,
          data: encodeFunctionData({
            abi: is1155 ? erc1155Abi : erc721Abi,
            functionName: 'safeTransferFrom',
            args: transferArgs,
          }),
        });

        const receipt = await waitForTransactionReceipt(wagmiConfig, {
          chainId: marketplaceChain.id,
          hash,
        });

        if (receipt.status !== 'success') throw new Error('Transaction failed');

        setTransactionHash(hash);
      } catch (e) {
        setError('An error occurred while transferring the token');
        console.error('transfer-error: ', e);
        throw e;
      }
    },
    [is1155, tokenId, walletClient, userAddress, contractAddress, marketplaceChain],
  );

  const Trigger = forwardRef((props, ref) =>
    cloneElement(trigger, {
      ref,
      ...props,
      onClick: () => setOpen(true),
    }),
  );

  const handleReset = useCallback(() => {
    resetForm();
    setTransactionHash(null);
    setError(null);
  }, [resetForm]);

  const handleClose = useCallback(() => {
    setOpen(false);
    handleReset();
    onClose?.(isSubmitSuccessful);
  }, [setOpen, handleReset, onClose, isSubmitSuccessful]);

  const status = isSubmitSuccessful
    ? 'success'
    : isSubmitting
    ? 'pending'
    : error
    ? 'error'
    : 'idle';

  const StatusComponent = transferStatusMap[status];

  return (
    <>
      <ActionDisabledTooltip action={ReservoirAction.Transfer}>
        <Trigger />
      </ActionDisabledTooltip>
      <Modal
        testId="transfer-modal"
        isOpen={isOpen}
        onClose={handleClose}
        maxW={rem(512)}
        p={0}
        closable={status !== 'pending'}
      >
        {status === 'idle' ? (
          <Box p="space.32">
            <Heading fontSize="2xl" fontWeight="normal">
              {t('transfer-modal.initial.title')}
            </Heading>
            <Text mt="space.12" color="gray.6">
              {t('transfer-modal.initial.description')}
            </Text>
            <chakra.form
              display="flex"
              flexDir="column"
              onSubmit={handleSubmit(onSubmit)}
              noValidate
              gap="space.20"
            >
              <FormControl mt="space.24" isInvalid={!!errors.address}>
                <FormLabel>{t('transfer-modal.initial.fields.address.label')}</FormLabel>
                <Input
                  {...register('address')}
                  type="text"
                  placeholder={t('transfer-modal.initial.fields.address.placeholder')}
                />
                <FormErrorMessage>{errors.address?.message}</FormErrorMessage>
              </FormControl>
              {is1155 && (ownedQuantity || 0) > 1 && (
                <FormControl isInvalid={!!errors.address}>
                  <QuantityPickerBox
                    onChange={quantity => {
                      setValue('quantity', quantity, {
                        shouldTouch: true,
                        shouldValidate: true,
                        shouldDirty: true,
                      });
                    }}
                    max={ownedQuantity || Infinity}
                    quantity={values.quantity}
                    tooltipText={t('transfer-modal.initial.fields.quantity.errors.max')}
                  />
                  <FormErrorMessage>{errors.quantity?.message}</FormErrorMessage>
                </FormControl>
              )}
              {showGasWarning && (
                <Flex py="space.12" px="space.18" rounded="radius.8" color="red" bg="darkRed">
                  <Text fontSize="xs">
                    {t('transfer-modal.gas-warning', {
                      deficit: gasFeeDeficit,
                      currency: marketplaceChain.nativeCurrency.symbol,
                    })}
                  </Text>
                </Flex>
              )}
              {chain?.id === marketplaceChain.id ? (
                <Button type="submit">{t('transfer-modal.initial.cta.transfer')}</Button>
              ) : (
                <Button onClick={() => switchChain?.({ chainId: marketplaceChain.id })}>
                  {t('transfer-modal.initial.cta.switch-chain', { chain: marketplaceChain.name })}
                </Button>
              )}
            </chakra.form>
          </Box>
        ) : (
          <StatusComponent
            tokenName={tokenName}
            quantity={values.quantity}
            ownedQuantity={ownedQuantity || 1}
            toAddress={values.address}
            onClose={handleClose}
            onReset={handleReset}
            transactionHash={transactionHash}
          />
        )}
      </Modal>
    </>
  );
};
